/**
 * Software detail page with version timeline and CVE information
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import {
  ArrowLeftIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PlusIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import {
  useGetSoftwareByIdQuery,
  useGetGroupedVersionsBySoftwareQuery,
  useGetCVEsBySoftwareQuery,
  useGetReleaseChannelsQuery,
} from '@/services/api';
import { SmartDownloadButton } from '@/components/SmartDownloadButton';
import { PlatformBadge } from '@/components/PlatformBadge';
import { CVEAnalysisPanel } from '@/components/CVEAnalysisPanel';
import { VersionDetailsSlideout } from '@/components/VersionDetailsSlideout';
import { VersionManageModal } from '@/components/VersionManageModal';
import { CVEManageModal } from '@/components/CVEManageModal';
import { EOLStatusPanel } from '@/components/EOLStatusPanel';

type TabType = 'versions' | 'vulnerabilities';
type SortOption = 'date-desc' | 'date-asc' | 'version-desc' | 'version-asc' | 'cve-desc' | 'cve-asc';

export default function SoftwareDetails() {
  const { id } = useParams<{ id: string }>();
  const softwareId = parseInt(id || '0');

  const [activeTab, setActiveTab] = useState<TabType>('versions');
  const [expandedCVEs, setExpandedCVEs] = useState<Set<number>>(new Set());
  const [selectedVersionNumber, setSelectedVersionNumber] = useState<string | null>(null);

  // CVE sync state
  const [isSyncingCVEs, setIsSyncingCVEs] = useState(false);
  const [syncResult, setSyncResult] = useState<{ success: boolean; message: string } | null>(null);

  // Version scraper state
  const [isScrapingVersions, setIsScrapingVersions] = useState(false);
  const [scraperResult, setScraperResult] = useState<{ success: boolean; message: string } | null>(null);

  // Individual version CVE sync state
  const [syncingVersionIds, setSyncingVersionIds] = useState<Set<number>>(new Set());

  // Version details slideout state
  const [isSlideoutOpen, setIsSlideoutOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<any>(null);

  // Authentication and authorization
  const { user, isSignedIn } = useUser();
  const isAdmin = isSignedIn && user?.publicMetadata?.role === 'admin';

  // Admin modal state
  const [isVersionModalOpen, setIsVersionModalOpen] = useState(false);
  const [editingVersion, setEditingVersion] = useState<any>(null);
  const [isCVEModalOpen, setIsCVEModalOpen] = useState(false);
  const [managingCVEsFor, setManagingCVEsFor] = useState<any>(null);

  // Sorting and filtering state
  const [sortBy, setSortBy] = useState<SortOption>('date-desc');
  const [filterOS, setFilterOS] = useState<Set<string>>(new Set());
  const [filterArch, setFilterArch] = useState<Set<string>>(new Set());
  const [filterReleaseChannel, setFilterReleaseChannel] = useState<string>('');
  const [showVerifiedOnly, setShowVerifiedOnly] = useState(false);

  const { data: software, isLoading: softwareLoading } = useGetSoftwareByIdQuery(softwareId);
  const { data: releaseChannelsData } = useGetReleaseChannelsQuery(softwareId);
  const { data: groupedVersionsData, isLoading: versionsLoading } = useGetGroupedVersionsBySoftwareQuery({
    software_id: softwareId,
    per_page: 100,
    ...(filterReleaseChannel && { release_channel: filterReleaseChannel }),
  });
  const { data: cves } = useGetCVEsBySoftwareQuery(softwareId);

  // Switch to version sort for packages — RHSA advisory dates don't reflect upstream version order
  useEffect(() => {
    if (software?.type === 'package') {
      setSortBy('version-desc');
    }
  }, [software?.type]);

  const releaseChannels = releaseChannelsData?.data || [];

  const versions = groupedVersionsData?.data || [];

  // Get unique OS platforms and architectures from all versions
  const availableOS = useMemo(() => {
    const osSet = new Set<string>();
    versions.forEach(v => v.platforms?.forEach(p => osSet.add(p.os_platform)));
    return Array.from(osSet).sort();
  }, [versions]);

  const availableArch = useMemo(() => {
    const archSet = new Set<string>();
    versions.forEach(v => v.platforms?.forEach(p => archSet.add(p.architecture)));
    return Array.from(archSet).sort();
  }, [versions]);

  // Filter and sort versions
  const filteredAndSortedVersions = useMemo(() => {
    let filtered = versions.filter(version => {
      // Filter by OS
      if (filterOS.size > 0) {
        const hasOS = version.platforms?.some(p => filterOS.has(p.os_platform));
        if (!hasOS) return false;
      }

      // Filter by architecture
      if (filterArch.size > 0) {
        const hasArch = version.platforms?.some(p => filterArch.has(p.architecture));
        if (!hasArch) return false;
      }

      // Filter by verified downloads
      if (showVerifiedOnly) {
        const hasVerified = version.platforms?.some(p => p.download_status === 'completed');
        if (!hasVerified) return false;
      }

      return true;
    });

    // Sort versions
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          return (b.release_date || '').localeCompare(a.release_date || '');
        case 'date-asc':
          return (a.release_date || '').localeCompare(b.release_date || '');
        case 'version-desc':
          return b.version_number.localeCompare(a.version_number, undefined, { numeric: true });
        case 'version-asc':
          return a.version_number.localeCompare(b.version_number, undefined, { numeric: true });
        case 'cve-desc':
          return (b.cve_count || 0) - (a.cve_count || 0);
        case 'cve-asc':
          return (a.cve_count || 0) - (b.cve_count || 0);
        default:
          return 0;
      }
    });

    return filtered;
  }, [versions, filterOS, filterArch, showVerifiedOnly, sortBy]);

  // Toggle filter helpers
  const toggleOSFilter = (os: string) => {
    setFilterOS(prev => {
      const newSet = new Set(prev);
      if (newSet.has(os)) {
        newSet.delete(os);
      } else {
        newSet.add(os);
      }
      return newSet;
    });
  };

  const toggleArchFilter = (arch: string) => {
    setFilterArch(prev => {
      const newSet = new Set(prev);
      if (newSet.has(arch)) {
        newSet.delete(arch);
      } else {
        newSet.add(arch);
      }
      return newSet;
    });
  };

  const clearAllFilters = () => {
    setFilterOS(new Set());
    setFilterArch(new Set());
    setFilterReleaseChannel('');
    setShowVerifiedOnly(false);
  };

  const activeFilterCount = filterOS.size + filterArch.size + (filterReleaseChannel ? 1 : 0) + (showVerifiedOnly ? 1 : 0);

  // Parse RHEL version from an RPM version string like "2.4.22-3.el9_3" → "RHEL 9.3"
  // Returns null if the version string doesn't contain an el suffix
  const parseRHELVersion = (versionNumber: string): string | null => {
    const match = versionNumber.match(/\.el(\d+)(?:[_.](\d+))?/);
    if (!match) return null;
    const major = match[1];
    const minor = match[2];
    return minor ? `RHEL ${major}.${minor}` : `RHEL ${major}`;
  };

  const isPackage = software?.type === 'package';

  // Track viewed CVEs using localStorage
  const viewedCVEsKey = `viewed-cves-${softwareId}`;
  const [viewedCVEs, setViewedCVEs] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(viewedCVEsKey);
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });

  // Memoize CVE IDs to prevent re-render loops
  const cveIds = useMemo(() => {
    return cves ? cves.map((cve) => cve.cve_id).join(',') : '';
  }, [cves]);

  // Mark CVEs as viewed when tab is switched to vulnerabilities
  useEffect(() => {
    if (activeTab !== 'vulnerabilities' || !cveIds) return;

    const cveIdArray = cveIds.split(',').filter(Boolean);
    const hasNewCVEs = cveIdArray.some(id => !viewedCVEs.has(id));

    // Only update if there are actually new CVEs to mark as viewed
    if (hasNewCVEs) {
      const newViewedCVEs = new Set([...viewedCVEs, ...cveIdArray]);
      setViewedCVEs(newViewedCVEs);
      localStorage.setItem(viewedCVEsKey, JSON.stringify([...newViewedCVEs]));
    }
  }, [activeTab, cveIds, viewedCVEsKey]);

  // Calculate new CVEs count
  const newCVEsCount = useMemo(() => {
    if (!cves) return 0;
    return cves.filter((cve) => !viewedCVEs.has(cve.cve_id)).length;
  }, [cves, viewedCVEs]);

  // Helper functions
  const toggleCVEExpansion = (cveId: number) => {
    setExpandedCVEs((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(cveId)) {
        newSet.delete(cveId);
      } else {
        newSet.add(cveId);
      }
      return newSet;
    });
  };

  const getSeverityColor = (severity: string | null) => {
    if (!severity) return 'badge-info';
    switch (severity.toLowerCase()) {
      case 'critical':
        return 'badge-critical';
      case 'high':
        return 'badge-high';
      case 'medium':
        return 'badge-medium';
      case 'low':
        return 'badge-low';
      default:
        return 'badge-info';
    }
  };

  // Handle clicking on CVE badge to switch to CVE tab and filter by version

  // Filter CVEs based on selected version number
  const filteredCVEs = selectedVersionNumber && cves
    ? cves.filter((cve: any) => {
        // Check if this CVE affects the selected version number
        // The API returns affected_version_numbers for each CVE
        return cve.affected_version_numbers?.includes(selectedVersionNumber);
      })
    : cves;

  // Get affected versions for a CVE
  const getAffectedVersionsForCVE = (cve: any) => {
    // The API now returns affected_version_ids, affected_version_numbers, and affected_version_statuses
    if (!cve.affected_version_ids || !cve.affected_version_numbers) {
      return [];
    }
    // Map to version objects with number and status info
    return cve.affected_version_ids.map((id: number, index: number) => ({
      id,
      version_number: cve.affected_version_numbers[index],
      status: cve.affected_version_statuses ? cve.affected_version_statuses[index] : 'affected',
    }));
  };

  // Handle CVE refresh
  const handleCVERefresh = async () => {
    // Check if user is authenticated admin
    if (!isAdmin) {
      setSyncResult({
        success: false,
        message: 'Admin access required',
      });
      return;
    }

    setIsSyncingCVEs(true);
    setSyncResult(null);

    try {
      // Microsoft Office products use custom CVE scraper
      const OFFICE_PRODUCT_IDS = [3, 32, 33, 34, 35]; // Removed 36 (Teams)
      const usesCustomScraper = OFFICE_PRODUCT_IDS.includes(softwareId);

      let response;
      if (usesCustomScraper) {
        // Route to Office CVE scraper (no POST body needed)
        response = await fetch('https://patch-manager-office-cves.peruzzijl.workers.dev/');
      } else {
        // Route to bulk CVE sync (queries NVD once, matches all versions locally)
        // This is much more efficient and accurate than per-version sync
        response = await fetch('https://patch-manager-cve-sync.peruzzijl.workers.dev/bulk-sync/software', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ software_id: softwareId }),
        });
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success) {
        if (usesCustomScraper) {
          // Office CVE scraper returns: cvesFound, cvesNew, versionsLinked
          setSyncResult({
            success: true,
            message: `Successfully synced CVEs: ${result.cvesNew} new, ${result.cvesFound} total found, ${result.versionsLinked} version links created`,
          });
        } else {
          // Bulk CVE sync returns: cvesProcessed, versionsProcessed
          setSyncResult({
            success: true,
            message: `Successfully synced CVEs: ${result.cvesProcessed} new CVEs processed across ${result.versionsProcessed} versions`,
          });
        }
        // Note: RTK Query will auto-refetch when tab is switched back to vulnerabilities
      } else {
        setSyncResult({
          success: false,
          message: `CVE sync failed: ${result.errors?.join(', ') || 'Unknown error'}`,
        });
      }
    } catch (error) {
      setSyncResult({
        success: false,
        message: `Error: ${String(error)}`,
      });
    } finally {
      setIsSyncingCVEs(false);
    }
  };

  // Handle version scraper
  const handleVersionScrape = async () => {
    // Check if user is authenticated admin
    if (!isAdmin) {
      setScraperResult({
        success: false,
        message: 'Admin access required',
      });
      return;
    }

    // Microsoft Office products (Word, Excel, PowerPoint, Outlook) use Office 365 scraper
    const OFFICE_PRODUCT_IDS = [3, 32, 33, 34, 35]; // Removed 36 (Teams)
    const isOfficeProduct = OFFICE_PRODUCT_IDS.includes(softwareId);

    // For Office products, use the shared Office scraper
    const scraperWorkerName = isOfficeProduct
      ? 'patch-manager-scraper-microsoft-office'
      : software?.scraper_worker_name;

    if (!scraperWorkerName) {
      setScraperResult({
        success: false,
        message: 'No scraper configured for this software',
      });
      return;
    }

    setIsScrapingVersions(true);
    setScraperResult(null);

    try {
      const scraperUrl = `https://${scraperWorkerName}.peruzzijl.workers.dev/`;

      const response = await fetch(scraperUrl, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success) {
        setScraperResult({
          success: true,
          message: `Successfully scraped versions: ${result.versionsNew || 0} new, ${result.versionsFound || 0} total found, ${result.downloadsQueued || 0} downloads queued`,
        });
        // Note: RTK Query will auto-refetch versions list
      } else {
        setScraperResult({
          success: false,
          message: `Scraper failed: ${result.errors?.join(', ') || result.error || 'Unknown error'}`,
        });
      }
    } catch (error) {
      setScraperResult({
        success: false,
        message: `Error: ${String(error)}`,
      });
    } finally {
      setIsScrapingVersions(false);
    }
  };

  // Handle CVE sync for individual version
  const handleVersionCVESync = async (versionId: number, versionNumber: string) => {
    // Check if user is authenticated admin
    if (!isAdmin) {
      console.error('Admin access required');
      return;
    }

    setSyncingVersionIds(prev => new Set(prev).add(versionId));

    try {
      const response = await fetch('https://patch-manager-cve-sync.peruzzijl.workers.dev/sync/version', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ version_id: versionId }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success) {
        // Show success feedback - you could use a toast notification here
        console.log(`✓ Synced CVEs for version ${versionNumber}: ${result.cvesFound} CVEs found`);
        // Force refetch of versions to update CVE count
        window.location.reload(); // Simple approach - you could use RTK Query invalidation instead
      } else {
        console.error(`✗ Failed to sync CVEs for version ${versionNumber}:`, result.errors);
        alert(`Failed to sync CVEs: ${result.errors?.join(', ') || 'Unknown error'}`);
      }
    } catch (error) {
      console.error(`Error syncing CVEs for version ${versionNumber}:`, error);
      alert(`Error syncing CVEs: ${String(error)}`);
    } finally {
      setSyncingVersionIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(versionId);
        return newSet;
      });
    }
  };

  const handleRerunAI = async (versionId: number) => {
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';
      const response = await fetch(`${API_BASE_URL}/admin/trigger-ai-indexing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ version_id: versionId }),
      });

      const result = await response.json();

      if (result.success) {
        alert(`AI analysis queued successfully. This may take a few minutes.`);
        // Optionally refresh after a delay
        setTimeout(() => window.location.reload(), 3000);
      } else {
        alert(`Failed to queue AI analysis: ${result.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error queuing AI analysis:', error);
      alert(`Error: ${String(error)}`);
    }
  };

  if (softwareLoading) {
    return (
      <div className="space-y-8">
        <div className="skeleton h-8 w-48" />
        <div className="card">
          <div className="flex items-start gap-6">
            <div className="skeleton w-24 h-24 rounded-xl" />
            <div className="flex-1 space-y-4">
              <div className="skeleton h-8 w-64" />
              <div className="skeleton h-4 w-full" />
              <div className="skeleton h-4 w-3/4" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!software) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="w-16 h-16 text-severity-critical mx-auto mb-4" />
        <h2 className="text-2xl font-bold mb-2">Software not found</h2>
        <Link to="/catalog" className="link">
          ← Back to catalog
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Back button */}
      <Link to="/catalog" className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
        <ArrowLeftIcon className="w-4 h-4" />
        Back to catalog
      </Link>

      {/* Software header */}
      <div className="card-glow">
        <div className="flex flex-col md:flex-row items-start gap-6">
          {/* Icon */}
          {software.icon_url ? (
            <img src={software.icon_url} alt={software.name} className="w-24 h-24 rounded-xl" />
          ) : (
            <div className="w-24 h-24 bg-cyber-blue-500/10 rounded-xl flex items-center justify-center">
              <ShieldCheckIcon className="w-12 h-12 text-cyber-blue-500" />
            </div>
          )}

          {/* Info */}
          <div className="flex-1">
            <div className="flex flex-wrap gap-2 mb-3">
              <span className="badge badge-info">{software.category}</span>
              {software.is_open_source && (
                <span className="badge bg-purple-500/10 text-purple-400 border border-purple-500/20">
                  Open Source
                </span>
              )}
              {software.quantum_ready_status === 'ready' && (
                <span className="badge bg-green-500/10 text-green-400 border border-green-500/20">
                  Quantum Ready
                </span>
              )}
              {software.quantum_ready_status === 'at-risk' && (
                <span className="badge badge-critical">Quantum At-Risk</span>
              )}
            </div>

            <h1 className="text-3xl font-bold mb-2">{software.name}</h1>
            <p className="text-lg text-gray-400 mb-4">{software.manufacturer}</p>

            {software.description && (
              <p className="text-gray-300 mb-6">{software.description}</p>
            )}

            {/* CPE Information */}
            {software.cpe_base && (
              <div className="bg-dark-bg-tertiary rounded-lg p-4 mb-6">
                <h3 className="text-sm font-semibold text-gray-400 mb-2">CPE 2.3 Identifier</h3>
                <code className="text-xs font-mono text-cyber-blue-400 break-all">
                  {software.cpe_base}
                </code>
              </div>
            )}

            {/* Links */}
            <div className="flex flex-wrap gap-4">
              {software.homepage_url && (
                <a
                  href={software.homepage_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary text-sm"
                >
                  Homepage
                </a>
              )}
              {software.documentation_url && (
                <a
                  href={software.documentation_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary text-sm"
                >
                  Documentation
                </a>
              )}
              {software.support_url && (
                <a
                  href={software.support_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary text-sm"
                >
                  Support
                </a>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="card text-center min-w-[120px]">
                <div className="text-2xl font-bold text-cyber-blue-500 mb-1">
                  {versions.length}
                </div>
                <div className="text-xs text-gray-400">Versions</div>
              </div>
              <div className="card text-center min-w-[120px]">
                <div className="text-2xl font-bold text-severity-critical mb-1">
                  {cves?.length || 0}
                </div>
                <div className="text-xs text-gray-400">CVEs</div>
              </div>
            </div>

            {/* Admin-Only Controls */}
            {isAdmin && (
              <>
                {/* Version Scraper Button */}
                <button
                  onClick={handleVersionScrape}
                  disabled={isScrapingVersions}
                  className="btn-primary text-sm w-full disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Check for new software versions"
                >
                  {isScrapingVersions ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Checking...
                    </>
                  ) : (
                    <>
                      <ClockIcon className="w-4 h-4 inline mr-1" />
                      Check for New Versions
                    </>
                  )}
                </button>

                {/* Scraper Result Message */}
                {scraperResult && (
                  <div
                    className={`text-xs p-3 rounded-lg border ${
                      scraperResult.success
                        ? 'bg-green-500/10 border-green-500/20 text-green-400'
                        : 'bg-red-500/10 border-red-500/20 text-red-400'
                    }`}
                  >
                    {scraperResult.message}
                  </div>
                )}

                {/* CVE Refresh Button */}
                <button
                  onClick={handleCVERefresh}
                  disabled={isSyncingCVEs}
                  className="btn-secondary text-sm w-full disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Sync CVEs from NVD for all versions"
                >
                  {isSyncingCVEs ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Syncing...
                    </>
                  ) : (
                    <>
                      <ShieldCheckIcon className="w-4 h-4 inline mr-1" />
                      Refresh CVEs
                    </>
                  )}
                </button>

                {/* Sync Result Message */}
                {syncResult && (
                  <div
                    className={`text-xs p-3 rounded-lg border ${
                      syncResult.success
                        ? 'bg-green-500/10 border-green-500/20 text-green-400'
                        : 'bg-red-500/10 border-red-500/20 text-red-400'
                    }`}
                  >
                    {syncResult.message}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* EOL Status */}
      <div className="card mb-6">
        <EOLStatusPanel productId={softwareId} productType="software" />
      </div>

      {/* Tabbed Content */}
      <div className="card">
        {/* Tab Navigation */}
        <div className="flex border-b border-dark-bg-tertiary mb-6">
          <button
            onClick={() => setActiveTab('versions')}
            className={`px-6 py-3 font-medium transition-colors relative ${
              activeTab === 'versions'
                ? 'text-cyber-blue-400'
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            Versions
            {activeTab === 'versions' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-cyber-blue-400" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('vulnerabilities')}
            className={`px-6 py-3 font-medium transition-colors relative ${
              activeTab === 'vulnerabilities'
                ? 'text-cyber-blue-400'
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            Known Vulnerabilities
            {newCVEsCount > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-green-500 text-white font-semibold">
                {newCVEsCount} new
              </span>
            )}
            {activeTab === 'vulnerabilities' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-cyber-blue-400" />
            )}
          </button>        </div>

        {/* Versions Tab */}
        {activeTab === 'versions' && (
          <div>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
              <h2 className="text-xl font-bold">
                Version History
                <span className="text-sm font-normal text-gray-400 ml-3">
                  {filteredAndSortedVersions.length} of {versions.length} versions
                </span>
              </h2>

              <div className="flex items-center gap-3">
                {/* Admin: Add Version button */}
                {isAdmin && (
                  <button
                    onClick={() => {
                      setEditingVersion(null);
                      setIsVersionModalOpen(true);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-cyber-blue-500 hover:bg-cyber-blue-600 text-white rounded-lg transition-colors text-sm font-medium"
                  >
                    <PlusIcon className="w-4 h-4" />
                    Add Version
                  </button>
                )}

                {/* Sort dropdown */}
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-400">Sort by:</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as SortOption)}
                    className="bg-dark-bg-tertiary border border-gray-700 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-cyber-blue-400"
                  >
                    <option value="date-desc">Release Date (Newest)</option>
                    <option value="date-asc">Release Date (Oldest)</option>
                    <option value="version-desc">Version (Newest)</option>
                    <option value="version-asc">Version (Oldest)</option>
                    <option value="cve-desc">CVE Count (Highest)</option>
                    <option value="cve-asc">CVE Count (Lowest)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="bg-dark-bg-tertiary rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-300">Filters</h3>
                {activeFilterCount > 0 && (
                  <button
                    onClick={clearAllFilters}
                    className="text-xs text-cyber-blue-400 hover:text-cyber-blue-300"
                  >
                    Clear all ({activeFilterCount})
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Release Channel filter */}
                {releaseChannels.length > 1 && (
                  <div>
                    <label className="text-xs text-gray-400 mb-2 block">Release Channel</label>
                    <select
                      value={filterReleaseChannel}
                      onChange={(e) => setFilterReleaseChannel(e.target.value)}
                      className="w-full text-xs px-3 py-1.5 rounded border bg-dark-bg-secondary border-gray-700 text-gray-300 hover:border-gray-600 focus:border-cyber-blue-500 focus:outline-none"
                    >
                      <option value="">All Channels</option>
                      {releaseChannels.map(channel => (
                        <option key={channel.release_channel} value={channel.release_channel}>
                          {channel.release_channel} ({channel.version_count})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* OS Platform filter */}
                {availableOS.length > 0 && (
                  <div>
                    <label className="text-xs text-gray-400 mb-2 block">Operating System</label>
                    <div className="flex flex-wrap gap-2">
                      {availableOS.map(os => (
                        <button
                          key={os}
                          onClick={() => toggleOSFilter(os)}
                          className={`text-xs px-3 py-1.5 rounded border transition-colors ${
                            filterOS.has(os)
                              ? 'bg-cyber-blue-500/20 border-cyber-blue-500 text-cyber-blue-300'
                              : 'bg-dark-bg-secondary border-gray-700 text-gray-400 hover:border-gray-600'
                          }`}
                        >
                          {os}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Architecture filter */}
                {availableArch.length > 0 && (
                  <div>
                    <label className="text-xs text-gray-400 mb-2 block">Architecture</label>
                    <div className="flex flex-wrap gap-2">
                      {availableArch.map(arch => (
                        <button
                          key={arch}
                          onClick={() => toggleArchFilter(arch)}
                          className={`text-xs px-3 py-1.5 rounded border transition-colors ${
                            filterArch.has(arch)
                              ? 'bg-cyber-blue-500/20 border-cyber-blue-500 text-cyber-blue-300'
                              : 'bg-dark-bg-secondary border-gray-700 text-gray-400 hover:border-gray-600'
                          }`}
                        >
                          {arch}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Verified downloads filter */}
                <div>
                  <label className="text-xs text-gray-400 mb-2 block">Download Status</label>
                  <button
                    onClick={() => setShowVerifiedOnly(!showVerifiedOnly)}
                    className={`text-xs px-3 py-1.5 rounded border transition-colors ${
                      showVerifiedOnly
                        ? 'bg-green-500/20 border-green-500 text-green-300'
                        : 'bg-dark-bg-secondary border-gray-700 text-gray-400 hover:border-gray-600'
                    }`}
                  >
                    {showVerifiedOnly && (
                      <svg className="w-3 h-3 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    )}
                    Verified Downloads Only
                  </button>
                </div>
              </div>
            </div>

            {/* Version list */}
            {versionsLoading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="skeleton h-24 w-full rounded-lg" />
                ))}
              </div>
            ) : filteredAndSortedVersions.length === 0 ? (
              <div className="text-center py-12 bg-dark-bg-tertiary rounded-lg">
                <p className="text-gray-400">
                  {versions.length === 0 ? 'No versions found' : 'No versions match the selected filters'}
                </p>
                {activeFilterCount > 0 && (
                  <button
                    onClick={clearAllFilters}
                    className="mt-4 text-cyber-blue-400 hover:text-cyber-blue-300 text-sm"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            ) : (
              <div className="bg-dark-bg-secondary rounded-lg overflow-hidden border border-cyber-blue-500/20">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-cyber-blue-500/20 bg-dark-bg-tertiary">
                        <th className="text-left py-3 px-4 text-gray-400 font-semibold">Version</th>
                        {isPackage && (
                          <th className="text-left py-3 px-4 text-gray-400 font-semibold">OS Version</th>
                        )}
                        <th className="text-left py-3 px-4 text-gray-400 font-semibold">
                          {isPackage ? 'Advisory Date' : 'Release Date'}
                        </th>
                        <th className="text-left py-3 px-4 text-gray-400 font-semibold">Channel</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-semibold">Platforms</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-semibold">Tags</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-semibold">CVEs</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-semibold">Patches</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAndSortedVersions.map((version) => {
                        // Create a stable unique key from version and platforms
                        const platformKeys = version.platforms?.map((p: any) => `${p.os_platform}-${p.architecture}`).sort().join(',') || 'no-platforms';
                        const uniqueKey = `${version.version_number}-${platformKeys}`;

                        return (
                        <tr
                          key={uniqueKey}
                          onClick={async () => {
                            setSelectedVersion(version);
                            setIsSlideoutOpen(true);
                          }}
                          className="border-b border-gray-800 hover:bg-dark-bg-tertiary/50 cursor-pointer transition-colors"
                        >
                          {/* Version */}
                          <td className="py-3 px-4">
                            <span className="font-mono text-cyber-blue-400 font-semibold">
                              v{isPackage
                                ? version.version_number.replace(/\.el\d+[_.]?\d*(\.\d+)?$/, '')
                                : version.version_number}
                            </span>
                          </td>

                          {/* RHEL Version badge (packages only) */}
                          {isPackage && (
                            <td className="py-3 px-4">
                              {(() => {
                                const rhelVer = parseRHELVersion(version.version_number);
                                if (!rhelVer) return <span className="text-gray-500">-</span>;
                                const isRhel9 = rhelVer.startsWith('RHEL 9');
                                return (
                                  <span className={`px-2 py-1 rounded text-xs font-medium border ${
                                    isRhel9
                                      ? 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                                      : 'bg-orange-500/10 text-orange-400 border-orange-500/20'
                                  }`}>
                                    {rhelVer}
                                  </span>
                                );
                              })()}
                            </td>
                          )}

                          {/* Advisory / Release Date */}
                          <td className="py-3 px-4 text-gray-300">
                            {version.release_date
                              ? new Date(version.release_date).toLocaleDateString()
                              : '-'}
                          </td>

                          {/* Channel */}
                          <td className="py-3 px-4">
                            {(version as any).release_channel ? (
                              <span className="px-2 py-1 bg-purple-500/10 text-purple-400 border border-purple-500/20 rounded text-xs">
                                {(version as any).release_channel}
                              </span>
                            ) : (
                              <span className="text-gray-500">-</span>
                            )}
                          </td>

                          {/* Platforms (OS Icons) */}
                          <td className="py-3 px-4">
                            <div className="flex gap-1">
                              {version.platforms && version.platforms.length > 0 ? (
                                Array.from(new Set(version.platforms.map((p: any) => p.os_platform))).map((os: any) => (
                                  <PlatformBadge key={os} os={os} arch="" />
                                ))
                              ) : (
                                <span className="text-gray-500">-</span>
                              )}
                            </div>
                          </td>

                          {/* Tags */}
                          <td className="py-3 px-4">
                            <div className="flex flex-wrap gap-1">
                              {!!version.is_latest && (
                                <span className="px-2 py-0.5 bg-green-500/10 text-green-400 border border-green-500/20 rounded text-xs">
                                  Latest
                                </span>
                              )}
                              {!!version.is_security_update && (
                                <span className="px-2 py-0.5 bg-red-500/10 text-red-400 border border-red-500/20 rounded text-xs">
                                  Security
                                </span>
                              )}
                            </div>
                          </td>

                          {/* CVE Count */}
                          <td className="py-3 px-4">
                            {(version.vulnerable_count ?? 0) > 0 ? (
                              <span className="px-2 py-1 bg-red-500/10 text-red-400 border border-red-500/20 rounded text-xs font-semibold">
                                {version.vulnerable_count} vulnerable
                              </span>
                            ) : (
                              <span className="text-gray-500 text-sm">0</span>
                            )}
                          </td>

                          {/* Patches */}
                          <td className="py-3 px-4">
                            {(version.fixed_count ?? 0) > 0 ? (
                              <span className="px-2 py-1 bg-green-500/10 text-green-400 border border-green-500/20 rounded text-xs font-semibold">
                                {version.fixed_count} fixed
                              </span>
                            ) : (
                              <span className="text-gray-500 text-sm">0</span>
                            )}
                          </td>

                          {/* Actions */}
                          <td className="py-3 px-4">
                            <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                              {isAdmin && version.platforms && version.platforms.length > 0 && (
                                <>
                                  {version.platforms.length === 1 ? (
                                    // Single platform - direct edit button
                                    <button
                                      onClick={() => {
                                        // Merge version-level data with platform-specific data
                                        const fullVersionData = {
                                          ...version.platforms[0],
                                          version_number: version.version_number,
                                          release_date: version.release_date,
                                          release_notes_url: version.release_notes_url,
                                          is_security_update: version.is_security_update,
                                          release_channel: (version as any).release_channel,
                                          display_version: (version as any).display_version,
                                          software_id: softwareId,
                                        };
                                        console.log('Editing version:', fullVersionData);
                                        setEditingVersion(fullVersionData);
                                        setIsVersionModalOpen(true);
                                      }}
                                      className="px-2 py-1 text-xs bg-purple-500/10 text-purple-400 border border-purple-500/20 rounded hover:bg-purple-500/20 transition-colors"
                                      title={`Edit ${version.platforms[0].os_platform} ${version.platforms[0].architecture}`}
                                    >
                                      <PencilIcon className="w-3 h-3" />
                                    </button>
                                  ) : (
                                    // Multiple platforms - dropdown selector
                                    <div className="relative group">
                                      <button
                                        className="px-2 py-1 text-xs bg-purple-500/10 text-purple-400 border border-purple-500/20 rounded hover:bg-purple-500/20 transition-colors flex items-center gap-1"
                                        title="Edit Platform Version"
                                      >
                                        <PencilIcon className="w-3 h-3" />
                                        <span className="text-xs">▼</span>
                                      </button>
                                      <div className="hidden group-hover:block absolute left-0 top-full mt-1 bg-dark-bg-tertiary border border-cyber-blue-500/20 rounded shadow-lg z-50 min-w-[150px]">
                                        {version.platforms.map((p: any, idx: number) => (
                                          <button
                                            key={idx}
                                            onClick={() => {
                                              // Merge version-level data with platform-specific data
                                              const fullVersionData = {
                                                ...p,
                                                version_number: version.version_number,
                                                release_date: version.release_date,
                                                release_notes_url: version.release_notes_url,
                                                is_security_update: version.is_security_update,
                                                release_channel: (version as any).release_channel,
                                                display_version: (version as any).display_version,
                                                software_id: softwareId,
                                              };
                                              console.log('Editing platform version:', fullVersionData);
                                              setEditingVersion(fullVersionData);
                                              setIsVersionModalOpen(true);
                                            }}
                                            className="block w-full text-left px-3 py-2 text-xs text-gray-300 hover:bg-cyber-blue-500/10 transition-colors whitespace-nowrap"
                                          >
                                            {p.os_platform} {p.architecture}
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  <button
                                    onClick={() => {
                                      setManagingCVEsFor(version.platforms[0]);
                                      setIsCVEModalOpen(true);
                                    }}
                                    className="px-2 py-1 text-xs bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 rounded hover:bg-yellow-500/20 transition-colors"
                                    title="Manage CVEs"
                                  >
                                    🔒
                                  </button>
                                  <button
                                    onClick={() => handleVersionCVESync(version.platforms[0].id, version.version_number)}
                                    disabled={syncingVersionIds.has(version.platforms[0].id)}
                                    className="px-2 py-1 text-xs bg-cyber-blue-500/10 text-cyber-blue-400 border border-cyber-blue-500/20 rounded hover:bg-cyber-blue-500/20 disabled:opacity-50 transition-colors"
                                    title="Sync CVEs"
                                  >
                                    {syncingVersionIds.has(version.platforms[0].id) ? '⟳' : '↻'}
                                  </button>
                                </>
                              )}
                              {version.release_notes_url && (
                                <a
                                  href={version.release_notes_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="px-2 py-1 text-xs bg-gray-500/10 text-gray-400 border border-gray-500/20 rounded hover:bg-gray-500/20 transition-colors"
                                  title="Release Notes"
                                >
                                  📄
                                </a>
                              )}
                              <SmartDownloadButton
                                platforms={version.platforms}
                                versionNumber={version.version_number}
                              />
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Version Details Slideout */}
        <VersionDetailsSlideout
          version={selectedVersion}
          isOpen={isSlideoutOpen}
          onClose={() => {
            setIsSlideoutOpen(false);
            setSelectedVersion(null);
          }}
          onRerunAI={handleRerunAI}
          isAdmin={isAdmin}
        />

        {/* Known Vulnerabilities Tab */}
        {activeTab === 'vulnerabilities' && (
          <div>
            <div className="flex flex-col gap-4 mb-6">
              {/* Title with count */}
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-bold">Known Vulnerabilities</h2>
                {cves && cves.length > 0 && (
                  <span className="px-3 py-1 text-sm rounded-lg bg-severity-critical/10 text-severity-critical border border-severity-critical/20 font-semibold">
                    {filteredCVEs?.length || 0} / {cves.length}
                  </span>
                )}
              </div>

              {/* Searchable version filter */}
              <div className="flex items-center gap-4">
                <label className="text-sm font-medium text-gray-400">Filter by version:</label>
                <select
                  value={selectedVersionNumber || ''}
                  onChange={(e) => setSelectedVersionNumber(e.target.value || null)}
                  className="input max-w-xs"
                >
                  <option value="">All versions</option>
                  {versions
                    .filter((v) => v.cve_count && v.cve_count > 0)
                    .sort((a, b) => b.version_number.localeCompare(a.version_number, undefined, { numeric: true }))
                    .map((v) => (
                      <option key={v.version_number} value={v.version_number}>
                        v{v.version_number} ({v.cve_count} CVE{v.cve_count !== 1 ? 's' : ''})
                      </option>
                    ))}
                </select>
              </div>
            </div>

            {!filteredCVEs || filteredCVEs.length === 0 ? (
              <div className="text-center py-12 bg-dark-bg-tertiary rounded-lg">
                <ShieldCheckIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Known Vulnerabilities</h3>
                <p className="text-gray-400">
                  This software has no known CVEs in our database.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredCVEs.map((cve) => {
                  const isExpanded = expandedCVEs.has(cve.id);
                  const affectedVersions = getAffectedVersionsForCVE(cve);

                  return (
                    <div
                      key={cve.id}
                      className="bg-dark-bg-tertiary rounded-lg border border-gray-800 hover:border-gray-700 transition-colors"
                    >
                      <div className="p-5">
                        {/* CVE Header */}
                        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-3">
                              <a
                                href={`https://nvd.nist.gov/vuln/detail/${cve.cve_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-mono font-bold text-lg text-cyber-blue-400 hover:text-cyber-blue-300 transition-colors"
                              >
                                {cve.cve_id}
                              </a>
                              <span className={`badge ${getSeverityColor(cve.severity)}`}>
                                {cve.severity ? cve.severity.toUpperCase() : 'UNKNOWN'}
                              </span>
                            </div>

                            {/* CVSS Score and Date */}
                            <div className="flex flex-wrap gap-4 text-sm mb-3">
                              {cve.cvss_score && (
                                <div>
                                  <span className="text-gray-500">CVSS Score:</span>
                                  <span className="ml-2 font-semibold text-white">
                                    {cve.cvss_score.toFixed(1)}
                                  </span>
                                </div>
                              )}
                              {cve.published_date && (
                                <div>
                                  <span className="text-gray-500">Published:</span>
                                  <span className="ml-2 text-gray-300">
                                    {new Date(cve.published_date).toLocaleDateString()}
                                  </span>
                                </div>
                              )}
                              {cve.last_modified_date && (
                                <div>
                                  <span className="text-gray-500">Modified:</span>
                                  <span className="ml-2 text-gray-300">
                                    {new Date(cve.last_modified_date).toLocaleDateString()}
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* Description */}
                            {cve.description && (
                              <p className="text-gray-300 leading-relaxed">
                                {cve.description}
                              </p>
                            )}
                          </div>

                          {/* External Link */}
                          <a
                            href={`https://nvd.nist.gov/vuln/detail/${cve.cve_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-secondary text-sm whitespace-nowrap"
                          >
                            View on NVD
                          </a>
                        </div>

                        {/* AI Analysis Panel */}
                        <CVEAnalysisPanel cveId={cve.cve_id} className="mt-4" />

                        {/* CWE Information */}
                        {cve.cwe_id && (
                          <div className="mb-4">
                            <span className="text-gray-500 text-sm">CWE:</span>
                            <span className="ml-2 text-sm font-mono text-gray-300">
                              {cve.cwe_id}
                            </span>
                          </div>
                        )}

                        {/* Affected Versions Accordion */}
                        <button
                          onClick={() => toggleCVEExpansion(cve.id)}
                          className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-300 transition-colors w-full py-2 border-t border-gray-800 mt-4"
                        >
                          {isExpanded ? (
                            <ChevronUpIcon className="w-4 h-4" />
                          ) : (
                            <ChevronDownIcon className="w-4 h-4" />
                          )}
                          <span className="font-medium">
                            Affected Versions
                            {affectedVersions.length > 0 && (
                              <span className="ml-2 text-gray-500">
                                ({affectedVersions.length})
                              </span>
                            )}
                          </span>
                        </button>

                        {/* Expanded Affected Versions */}
                        {isExpanded && (
                          <div className="mt-3 pl-6">
                            {affectedVersions.length === 0 ? (
                              <p className="text-sm text-gray-500 italic">
                                Affected version information is being gathered. Please check the NVD
                                link for details.
                              </p>
                            ) : (
                              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                                {/* Group versions by version_number and status to avoid showing duplicates for each platform */}
                                {Object.entries(
                                  affectedVersions.reduce((acc: any, version: any) => {
                                    const key = `${version.version_number}-${version.status}`;
                                    if (!acc[key]) {
                                      acc[key] = version;
                                    }
                                    return acc;
                                  }, {})
                                ).map(([key, version]: [string, any]) => {
                                  const isFixed = version.status === 'fixed';
                                  const isAffected = version.status === 'affected';

                                  return (
                                    <div
                                      key={key}
                                      className={`text-sm rounded px-3 py-2 font-mono flex items-center justify-between gap-2 ${
                                        isFixed
                                          ? 'bg-green-900/30 border border-green-700/50 text-green-400'
                                          : isAffected
                                          ? 'bg-red-900/30 border border-red-700/50 text-red-400'
                                          : 'bg-dark-bg-secondary text-cyber-blue-400'
                                      }`}
                                    >
                                      <span>v{version.version_number}</span>
                                      {isFixed && (
                                        <span className="text-xs bg-green-700/50 px-1.5 py-0.5 rounded">
                                          FIXED
                                        </span>
                                      )}
                                      {isAffected && (
                                        <span className="text-xs bg-red-700/50 px-1.5 py-0.5 rounded">
                                          VULNERABLE
                                        </span>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}

                        {/* References */}
                        {cve.references && Array.isArray(cve.references) && cve.references.length > 0 && (
                          <div className="mt-4 pt-4 border-t border-gray-800">
                            <h4 className="text-sm font-semibold text-gray-400 mb-2">References:</h4>
                            <div className="space-y-1">
                              {cve.references.slice(0, 5).map((ref, idx) => (
                                <a
                                  key={idx}
                                  href={ref}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block text-xs text-cyber-blue-400 hover:text-cyber-blue-300 truncate"
                                >
                                  {ref}
                                </a>
                              ))}
                              {cve.references.length > 5 && (
                                <p className="text-xs text-gray-500">
                                  +{cve.references.length - 5} more references
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Admin Modals */}
        {isAdmin && (
          <>
            <VersionManageModal
              isOpen={isVersionModalOpen}
              onClose={() => {
                setIsVersionModalOpen(false);
                setEditingVersion(null);
              }}
              softwareId={softwareId}
              softwareName={software?.name || ''}
              version={editingVersion}
            />

            {managingCVEsFor && (
              <CVEManageModal
                isOpen={isCVEModalOpen}
                onClose={() => {
                  setIsCVEModalOpen(false);
                  setManagingCVEsFor(null);
                }}
                version={managingCVEsFor}
                existingCVEs={
                  cves
                    ?.filter((cve: any) =>
                      cve.affected_version_numbers?.includes(managingCVEsFor.version_number)
                    )
                    .map((cve: any) => ({
                      cve_id: cve.cve_id,
                      status: 'affected', // You can enhance this to check actual status
                    })) || []
                }
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
